/**
 * PRP-MCP-12 work item 1 (G3): suggestions sent from chat, over HTTP.
 *
 * The user's own list (`/mcp/suggestions`, the /assistant page) and the
 * founder's queue (`/mcp/admin/suggestions`, admin only on the server). Its own
 * module, like `adminReviewApi.ts`: `api.ts` sits at its god-file ceiling. The
 * shared `api` instance attaches the Firebase token and unwraps `response.data`.
 */
import { api } from './api';

export type SuggestionStatus = 'new' | 'planned' | 'built' | 'declined';
export type SuggestionKind = 'improvement' | 'macro';

export interface SuggestionReply {
  text: string;
  at: string;
  emailed?: boolean;
}

/** One row of the user's own list. */
export interface MySuggestion {
  ref: string;
  created_at: string;
  kind: SuggestionKind;
  status: SuggestionStatus;
  status_label: string;
  what_you_asked: string;
  replies: SuggestionReply[];
}

/** `S-0042_WET_KITCHEN.MCX`, the same name the server and scripts/mcp12_fetch_candidates.py use. */
export function macroFileName(ref: string, name: string | undefined): string {
  const stem =
    (name ?? 'MACRO')
      .toUpperCase()
      .replace(/[^A-Z0-9_-]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60) || 'MACRO';
  return `${ref}_${stem}.MCX`;
}

/** One row of the admin queue. */
export interface SuggestionRow {
  id: string;
  ref: string;
  user_id: string;
  user_email: string | null;
  kind: SuggestionKind;
  status: SuggestionStatus;
  chat_app: string | null;
  what_you_asked: string;
  after_nudge: boolean;
  reply_count: number;
  created_at: string;
  updated_at: string;
}

export interface SuggestionTrailEntry {
  tool: string;
  outcome: string;
}

export interface SuggestionMacroLine {
  cat?: string;
  sel?: string;
  description?: string;
  action?: string;
  quantity?: number | null;
  calc?: string | null;
  unit?: string;
  heading?: string | null;
  note?: string | null;
}

export interface SuggestionDetail extends SuggestionRow {
  what_happened: string | null;
  what_you_expected: string | null;
  estimate_id: string | null;
  room: string | null;
  applies: string | null;
  applies_label: string | null;
  notes: string | null;
  trail: SuggestionTrailEntry[];
  snapshot: { rooms?: { name: string; lines: unknown[] }[] } | null;
  macro: { name?: string; description?: string | null; lines?: SuggestionMacroLine[] } | null;
  replies: SuggestionReply[];
  user_admin_link: string;
  link: string;
  emailed?: boolean;
}

export interface SuggestionListParams {
  status?: SuggestionStatus;
  user?: string;
  limit?: number;
  offset?: number;
}

export const SuggestionsAPI = {
  /** The signed-in user's own suggestions, newest first. */
  mine: (): Promise<{ items: MySuggestion[] }> => api.get('/mcp/suggestions'),

  /** The admin queue, newest first. */
  list: (params: SuggestionListParams = {}): Promise<{ items: SuggestionRow[]; total: number; statuses: string[] }> =>
    api.get('/mcp/admin/suggestions', { params }),

  get: (id: string): Promise<SuggestionDetail> => api.get(`/mcp/admin/suggestions/${encodeURIComponent(id)}`),

  /** Built or declined emails the user (with the reply, if one is given). */
  setStatus: (id: string, status: SuggestionStatus, reply?: string): Promise<SuggestionDetail> =>
    api.post(`/mcp/admin/suggestions/${encodeURIComponent(id)}/status`, reply ? { status, reply } : { status }),

  /** A shared macro (kind macro) as a Xactimate .MCX file, no prices. The interceptor unwraps to the Blob. */
  macroFile: (id: string): Promise<Blob> =>
    api.get(`/mcp/admin/suggestions/${encodeURIComponent(id)}/macro.mcx`, { responseType: 'blob' }),

  /** Emails the user the reply. */
  reply: (id: string, text: string): Promise<SuggestionDetail> =>
    api.post(`/mcp/admin/suggestions/${encodeURIComponent(id)}/reply`, { text }),
};
